import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, ApiError } from '@/lib/api/client';

interface User {
  id: string;
  email: string;
  name: string | null;
  tenantId: string;
  tenantName: string;
  role: string;
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  loading: boolean;
  error: string | null;
  requestMagicLink: (email: string) => Promise<boolean>;
  verifyToken: (token: string) => Promise<boolean>;
  logout: () => void;
  updateUser: (updates: Partial<User>) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  // Check for existing session on mount
  useEffect(() => {
    const checkAuth = async () => {
      const storedToken = localStorage.getItem('foxgrid_token');
      if (!storedToken) {
        setLoading(false);
        return;
      }

      setToken(storedToken);

      try {
        const response = await api.getCurrentUser();
        if (response.success && response.data) {
          setUser(response.data);
        } else {
          localStorage.removeItem('foxgrid_token');
          setToken(null);
        }
      } catch (err) {
        // Only clear token on 401 (unauthorized) - means token is invalid
        // Don't clear on network errors or server errors (API might be restarting)
        if (err instanceof ApiError && err.statusCode === 401) {
          localStorage.removeItem('foxgrid_token');
          setToken(null);
        } else {
          // Keep the token, might be a temporary API issue
          console.warn('Auth check failed (keeping token):', err);
        }
      } finally {
        setLoading(false);
      }
    };

    checkAuth();
  }, []);

  const requestMagicLink = useCallback(async (email: string): Promise<boolean> => {
    setError(null);
    try {
      await api.requestMagicLink(email);
      return true;
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError('Failed to send magic link');
      }
      return false;
    }
  }, []);

  const verifyToken = useCallback(
    async (magicToken: string): Promise<boolean> => {
      setError(null);
      try {
        const response = await api.verifyToken(magicToken);
        if (response.success) {
          localStorage.setItem('foxgrid_token', response.accessToken);
          setToken(response.accessToken);
          setUser(response.user);
          navigate('/dashboard');
          return true;
        }
        return false;
      } catch (err) {
        if (err instanceof ApiError) {
          setError(err.message);
        } else {
          setError('Failed to verify magic link');
        }
        return false;
      }
    },
    [navigate]
  );

  const logout = useCallback(() => {
    localStorage.removeItem('foxgrid_token');
    setToken(null);
    setUser(null);
    navigate('/login');
  }, [navigate]);

  const updateUser = useCallback((updates: Partial<User>) => {
    setUser((prev) => (prev ? { ...prev, ...updates } : null));
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        loading,
        error,
        requestMagicLink,
        verifyToken,
        logout,
        updateUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
